"use client";

import type { ReactNode } from "react";

interface ContactFieldProps {
  /** Field name — seeds every generated id, so it must be unique per form. */
  name: string;
  label: string;
  /** Translated message. Presence is what marks the field invalid. */
  error?: string;
  /** Pass the translated word for "optional" to mark the field optional. */
  optionalLabel?: string;
  help?: string;
  /**
   * Rendered on the help row, trailing edge. Deliberately outside
   * `aria-describedby`: a countdown that changes on every keystroke would be
   * re-read every time focus returned to the field, and it speaks for itself
   * through its own live region.
   */
  counter?: ReactNode;
  children: (ids: {
    id: string;
    describedBy: string | undefined;
    invalid: boolean;
  }) => ReactNode;
}

/**
 * One label + control + message row.
 *
 * The control is a render prop rather than a child element because only the
 * control can carry `id`, `aria-invalid`, and `aria-describedby`, and wiring
 * those by hand at six call sites is how one of them ends up missing. Help text
 * and the error message are both referenced by `aria-describedby`, in that
 * order, so a screen reader reads the requirement before the violation.
 */
export function ContactField({
  name,
  label,
  error,
  optionalLabel,
  help,
  counter,
  children,
}: ContactFieldProps) {
  const id = `contact-${name}`;
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(" ") || undefined;

  return (
    <div>
      <label
        htmlFor={id}
        className="mb-2 flex items-baseline gap-2 text-sm font-medium text-site-foreground"
      >
        {label}
        {/*
          The leading space is load-bearing: the flex gap separates these
          visually, but the accessible name is computed from text content, where
          without it the field announces as "Phone number(optional)".
        */}
        {optionalLabel && (
          <span className="text-xs font-normal text-site-foreground/60">
            {` (${optionalLabel})`}
          </span>
        )}
      </label>

      {children({ id, describedBy, invalid: Boolean(error) })}

      {(help || counter) && (
        <div className="mt-2 flex items-start justify-between gap-4">
          {help ? (
            <p id={helpId} className="text-xs text-site-foreground/60">
              {help}
            </p>
          ) : (
            <span />
          )}
          {counter}
        </div>
      )}
      {error && (
        <p id={errorId} className="mt-2 text-sm text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}
